import {Component, OnDestroy, OnInit} from '@angular/core';
import {FormControl, FormGroup} from "@angular/forms";

import {
  NotificationDialogComponent
} from "../../../../shared/components/dialogs/notification-dialog/notification-dialog.component";
import {BoosterMode, BoosterService} from "../services/booster.service";
import {distinctUntilChanged, filter, first, switchMap, takeUntil} from "rxjs/operators";
import {Store} from "@ngrx/store";
import * as fromStore from "../../../../store";
import {DbStoreState} from "../../../../store";
import {combineLatest, Subject} from "rxjs";
import {NotifierComponent} from "../../../../shared/components/notifier/notifier.component";
import {Notification} from "../../../../shared/components/notifier/notification";
import {NotificationType} from "../../../../shared/components/notifier/notification-type";
import {TypeUtils} from "../../../../shared/utils/type-utils";
import {GoogleAnalyticsService} from "../../../../shared/services/google-analytics/google-analytics.service";
import {TestModeConfirmationComponent} from "../dialogs/test-mode-confiramtion/test-mode-confirmation.component";
import {DbModalService} from "../../../../modules/modal/modal";
import {BoosterUpdateComponent} from "../dialogs/booster-update-dialog/booster-update.component";
import {UserSubscription, UserSubscriptionCategory} from "../../../../user/user-subscription";
import {ActivatedRoute, Router} from "@angular/router";
import {DialogService} from "../../../../../ng2-bootstrap-modal/dialog.service";
import {TextUtils} from "../../../../shared/utils/text-utils";
import {Website} from "../../website";
import {Utils} from "../../../../shared/utils/utils";
import {Config} from "../../../../config/config";
import {ActivityAction, ActivityStatus} from "../../../../activities/activity";
import {
  OneWeekFreeDemoUpgradeComponent
} from "../../../../shared/components/dialogs/one-month-free/components/one-week-free-demo-upgrade/one-week-free-demo-upgrade.component";
import {BuilderSectionsInteractionService} from "../../../../builder-v2/builder-sections-interaction.service";


@Component({
    selector: 'db-settings',
    templateUrl: './settings.component.html',
    styleUrls: ['./settings.component.scss'],
    standalone: false
})
export class SettingsComponent implements OnInit, OnDestroy {

  public website: Website;
  public currentSubscription: UserSubscription;
  public testMode: 'on' | 'off' | null;
  public testModeEnabled: boolean;
  public boosterMode: BoosterMode;
  public boosterModeLoading: boolean = true;
  public cachingStatus: boolean = false;
  public cacheEnabled: boolean;
  public lastClearedCacheDate: number;
  public settingsForm: FormGroup = new FormGroup({
    'optimizationMode': new FormControl(),
    'websiteCachingStatus': new FormControl(this.cachingStatus),
    'testMode': new FormControl()
  });
  public testModeSwitches: boolean;
  public cacheSwitches: boolean;
  public loadingClearCache: boolean;
  public loadingPageClearCache: boolean;
  public loadingTestMode: boolean;
  public hasTestMode: boolean;
  public toggleKnowledgeBase: boolean;
  private _destroyStream$: Subject<boolean> = new Subject<boolean>();
  public UserSubscriptionCategory: any = UserSubscriptionCategory;
  public notConnected: boolean;
  public showPercentOffer: boolean;
  public demoUser: boolean = false;
  public boosterPluginIsActive: boolean = false;
  public boosterPluginIsOld: boolean;
  public boosterUpdateAvailable: boolean;
  public loadingPage: boolean;
  public companyName: string = 'Dashboard';

  constructor(
    private _dialogService: DialogService,
    private _boosterService: BoosterService,
    private _store: Store<DbStoreState>,
    private _dbModalService: DbModalService,
    private _googleAnalyticsService: GoogleAnalyticsService,
    private _router: Router,
    private _route: ActivatedRoute,
    private _modalService: DbModalService,
    private _builderSectionsInteractionService: BuilderSectionsInteractionService
  ) {
  }

  ngOnInit() {
    this.website = this._route.snapshot.parent.parent.data['website'];
    if (!TypeUtils.isNull(this._route.snapshot.data['boosterTroubleshootData'])) {
      const disconnected = this._route.snapshot.data['boosterTroubleshootData'] === 'alive_but_not_connected';
      const restNoRoute = this._route.snapshot.data['boosterTroubleshootData'] === 'error';
      this.notConnected = disconnected || restNoRoute;
    }
    this._getOngoingActivities();
    this._getUserAndSubscriptionData();
    this._getBoosterData();
    this._showPercentOffer();
    this._checkBoosterPluginUpdateAvailable();
    this._getCompanyInfo();
  }

  private _getCompanyInfo(): void {
    this._builderSectionsInteractionService.companyInfo
      .pipe(
        filter(companyInfo => !!companyInfo),
        takeUntil(this._destroyStream$),
        first()
      )
      .subscribe(companyInfo => {
        this.companyName = companyInfo?.branding?.company_name || companyInfo?.agency?.name || 'YourBrand';
      });
  }

  private _getOngoingActivities(): void {
    this._store.select(fromStore.getOngoingActivity)
      .pipe(
        first(activity => activity
          && activity.action === ActivityAction.UPDATE
          && activity.meta.product_data.slug === Config.optimizerPluginSlug
          && (activity.status === ActivityStatus.DONE || activity.status === ActivityStatus.FAILED))
      )
      .subscribe(activity => {
        this.loadingPage = false;
      });
  }

  private _showPercentOffer() {
    const percentOfferEndDate = new Date("2024-01-30 18:00:00").getTime();
    const datesDiff = Utils.getTimeDiffFromToday(percentOfferEndDate);
    this.showPercentOffer = datesDiff && this.currentSubscription && this.currentSubscription.trialBoosterClient;
  }

  private _getUserAndSubscriptionData(): void {
    this._store.select(fromStore.getUserLoaded)
      .pipe(
        filter((loaded: boolean) => loaded),
        switchMap(() => combineLatest(
          this._store.select(fromStore.getUser),
          this._store.select(fromStore.getUserSubscription)
        )),
        takeUntil(this._destroyStream$)
      )
      .subscribe(([user, userSubscription]) => {
        this.currentSubscription = userSubscription;
        this.demoUser = Utils.isDemoUser(userSubscription, user);
      });
  }

  private _getTestMode(): void {
    this._store.select(fromStore.getWebsiteBoosterTestMode(this.website?.id))
      .pipe(
        distinctUntilChanged((oldTestModeVal: any, newTestModeVal: any) => oldTestModeVal && newTestModeVal && oldTestModeVal === newTestModeVal),
        takeUntil(this._destroyStream$)
      )
      .subscribe(testMode => {
        if (!TypeUtils.isNull(testMode)) {
          this.testMode = testMode;
          this.testModeEnabled = !TypeUtils.isNull(this.testMode) && this.testMode === 'on';

          this.hasTestMode = !TypeUtils.isNull(this.testMode);
          this.settingsForm.controls['testMode'].setValue(this.testModeEnabled);
          setTimeout(() => {
            if (this.testModeSwitches) {
              let noticeMessage = !this.testMode ? "Your test mode is turned on." : "Your test mode is turned off.";
              NotifierComponent.notify(new Notification(noticeMessage, NotificationType.SUCCESS));
              this.testModeSwitches = false;
            }
            this.loadingTestMode = false;
          }, 3000);
        }
      });
  }

  private _checkBoosterPluginUpdateAvailable(): void {
    this._store.select(fromStore.getBoosterPluginInfo(this.website?.id))
      .pipe(
        filter(pluginInfo => !!pluginInfo)
      )
      .subscribe(([pluginUpdateAvailable, currentVersion]) => {
        if (!TypeUtils.isNull(pluginUpdateAvailable) && !!pluginUpdateAvailable) {
          this.boosterUpdateAvailable = true;
        }
      });
  }

  private _getBoosterMode(): void {
    this._store.select(fromStore.getWebsiteBoosterModeLevel(this.website?.id))
      .pipe(
        distinctUntilChanged((oldModeLevelVal: any, newModeLevelVal: any) => oldModeLevelVal && newModeLevelVal && oldModeLevelVal === newModeLevelVal),
        takeUntil(this._destroyStream$)
      )
      .subscribe(modeLevel => {
        if (!TypeUtils.isNull(modeLevel)) {
          this.boosterMode = TypeUtils.isDefined(modeLevel) ? modeLevel : null;
          this.boosterModeLoading = false;
        }
      });
  }

  private _getBoosterCacheStatus(): void {
    this._store.select(fromStore.getWebsiteCacheStatus(this.website?.id))
      .pipe(
        distinctUntilChanged((cacheStatus1: any, cacheStatus2: any) => cacheStatus1 && cacheStatus2 && +cacheStatus1.cacheDate === +cacheStatus2.cacheDate),
        takeUntil(this._destroyStream$)
      )
      .subscribe(cacheStatus => {
        if (!TypeUtils.isNull(cacheStatus)) {
          this.cachingStatus = cacheStatus.cacheEnabled;
          this.lastClearedCacheDate = cacheStatus.cacheDate;
          this.settingsForm.controls['websiteCachingStatus'].setValue(this.cachingStatus);
        }
      });
  }

  private _getBoosterData(): void {
    this._getTestMode();
    this._getBoosterMode();
    this._getBoosterCacheStatus();
    this._checkBoosterState();
  }

  private _checkBoosterState(): void {
    this._store.select(fromStore.getWebsiteOptimizerPlugin(this.website?.id))
      .pipe(
        takeUntil(this._destroyStream$)
      )
      .subscribe((optimizerPlugin): void => {
        this.boosterPluginIsOld = TypeUtils.isEmpty(optimizerPlugin);
        if (optimizerPlugin && !TypeUtils.isEmpty(optimizerPlugin)) {
          this.boosterPluginIsActive = optimizerPlugin.active;
        }
      });
  }

  public toggleKnowLedgeBase() {
    this.toggleKnowledgeBase = !this.toggleKnowledgeBase;
    this._store.dispatch(new fromStore.ToggleKnowledgeBase(this.toggleKnowledgeBase ? {articleId: 7204540443154, scrollToEl: ''} : false));
  }


  public disableTestMode(): void {
    this.loadingTestMode = true;
    const testModeStatus: 'on' | 'off' = this.testMode === 'off' ? 'on' : 'off';
    this._boosterService.setTestMode(this.website?.id, testModeStatus)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
        this._store.dispatch(new fromStore.LoadBoosterOptimizeStatus({
          websiteId: this.website?.id,
          pageUrl: this.website?.site_url
        }));
        if (response.success) {
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
      });
  }

  public clearCache(): void {
    if (this.demoUser) {
      const analyticsData = {
        eventCategory: 'Free Upgrade Offer',
        eventAction: 'Locked button click',
        eventLabel: 'Clear cache'
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this.showDemoUpgradeDialog();
      return;
    } else {
      this._dialogService.addDialog(NotificationDialogComponent, {
        header: TextUtils.getText('text_clearCacheDialog'),
        message: TextUtils.getText('text_clearCacheDescDialog'),
        buttonText: TextUtils.getText('text_clearCacheBtnDialog'),
        showCancelButton: true,
        action: 'clear-cache'
      })
        .subscribe((confirmed: string): void => {
          if (confirmed && confirmed === 'clear-cache') {
            this._clearCache();
          }
        });
    }
  }

  public restore(): void {
    this._dialogService.addDialog(NotificationDialogComponent, {
      header: TextUtils.getText('text_restoreOriginalImages'),
      message: TextUtils.getText('text_restoreOriginalImagesDesc'),
      buttonText: TextUtils.getText('text_restore'),
      showCancelButton: true,
      action: 'restore'
    })
      .subscribe((confirmed: string): void => {
        if (confirmed && confirmed === 'restore') {
        }
      });
  }

  public upgrade(): void {
    this._router.navigate(['/upgrade-plan']);
  }

  public websiteCaching(): void {
    this.cacheSwitches = true;
    this.loadingPageClearCache = true;
    this.cachingStatus = !this.cachingStatus;
    this.cacheEnabled = this.cachingStatus;
    this._boosterService.pageCache(this.website?.id, this.cachingStatus)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
          this._store.dispatch(new fromStore.LoadBoosterOptimizeStatus({
            websiteId: this.website?.id,
            pageUrl: this.website?.site_url
          }));
          this.settingsForm.controls['websiteCachingStatus'].setValue(this.cachingStatus);
          this.loadingPageClearCache = false;
          let noticeMessage = !!this.cachingStatus ? "text_site_cache_enabled" : "text_site_cache_disabled";
          NotifierComponent.notify(new Notification(TextUtils.getText(noticeMessage), NotificationType.SUCCESS));
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
      });
  }

  public websiteTestMode(): void {
    if (this.demoUser) {
      const analyticsData = {
        eventCategory: 'Free Upgrade Offer',
        eventAction: 'Locked button click',
        eventLabel: 'Test mode'
      };
      this._googleAnalyticsService.sendAnalyticsEventWithCore(analyticsData);
      this.showDemoUpgradeDialog()
      this.settingsForm.controls['testMode'].setValue(false);
      return;
    } else {
      if (this.hasTestMode) {
        if (this.testMode === 'on') {
          this.testModeSwitches = true;
          this.disableTestMode();
        } else {
          this.settingsForm.controls['testMode'].setValue(false);
          const modal = this._dbModalService.open(TestModeConfirmationComponent);
          modal.componentInstance.websiteId = this.website && this.website?.id;
          modal.componentInstance.testMode = 'on';
          modal.closed.subscribe(() => {
            this.loadingTestMode = false;
            this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website?.id}));
            this._store.dispatch(new fromStore.LoadBoosterOptimizeStatus({
              websiteId: this.website.id,
              pageUrl: this.website.site_url
            }));
          });
          modal.dismissed.subscribe(() => {
            this.loadingTestMode = false;
          });
        }
      } else {
        this.settingsForm.controls['testMode'].setValue(false);
        const modal = this._dbModalService.open(BoosterUpdateComponent);
        modal.componentInstance.websiteId = this.website && this.website.id;
        modal.componentInstance.adminPluginsUrl = this.website && this.website.adminPluginsUrl;
        modal.componentInstance.websiteConnectedFrom = this.website && this.website.connectedFrom;
      }
    }
  }

  ngOnDestroy() {
  }

  public selectMode(mode: BoosterMode): void {
    this.boosterMode = mode;
  }

  private _clearCache(): void {
    this.loadingClearCache = true;
    this._boosterService.clearCache(this.website.id)
      .pipe(
        first(response => !!response, null)
      )
      .subscribe(response => {
        if (response.success) {
          this._store.dispatch(new fromStore.LoadBoosterData({websiteId: this.website.id}));
          this._store.dispatch(new fromStore.LoadBoosterOptimizeStatus({
            websiteId: this.website.id,
            pageUrl: this.website.site_url
          }));
          setTimeout(() => {
            this.loadingClearCache = false;
            NotifierComponent.notify(new Notification(TextUtils.getText('text_site_cache_cleared'), NotificationType.SUCCESS));
          }, 2000);
        } else {
          NotifierComponent.notify(new Notification(TextUtils.getText('text_somethingWentWrongDot'), NotificationType.DANGER));
        }
      });
  }

  showDemoUpgradeDialog() {
    const firstTime = TypeUtils.isNull(localStorage.getItem('dbShowFirstTime'));
    const options = {
      overflowHidden: false,
    };
    if (!firstTime) {
      options['width'] = 1240;
    } else {
      options['width'] = 460;
      options['height'] = 600;
    }
    const modal = this._modalService.open(OneWeekFreeDemoUpgradeComponent, options);
    modal.componentInstance.showFirstTime = firstTime;
  }

}
